import { JSX } from 'react';
import { StructuredLogViewer } from './StructuredLogViewer';

/**
 * Format the log details sent by API to show in readable format
 * @param {string} logMessage is log details of notification/queue
 * @param {function} t is the translation function
 * @returns {JSX.Element} the div element
 */
export const formatLogContent = (logMessage: string, t: any): JSX.Element => {
  let logText = null;

  if (logMessage === null) {
    return <div className="pt-1">{t('messages.noDataFound')}</div>;
  } else {
    // Removes all opening and closing { } brackets in message string
    // Split the string into individual lines using the '\n' delimiter
    const logLines = logMessage.replaceAll(/[{}]/g, '').split('\n');

    // Create an array of JSX elements, each representing a line
    logText = logLines?.map((line, index) => <div key={index + 1}>{line}</div>);

    return <div className="pt-1">{logText}</div>;
  }
};

/**
 * Format a JSON-structured log with a tabbed view — one tab per activity key
 * plus an "All" tab showing everything. Falls back to plain rendering for non-JSON.
 * @param {string} logMessage is log details from the ProductRelease API
 * @param {function} t is the translation function
 * @returns {JSX.Element} the div element
 */
export const formatStructuredLogContent = (logMessage: string, t: any): JSX.Element => {
  if (logMessage === null) {
    return <div className="pt-1">{t('messages.noDataFound')}</div>;
  }

  try {
    // Escape raw control characters that make JSON.parse fail when they appear
    // literally inside string values (e.g. embedded newlines from the API).
    const sanitized = logMessage
      .split('\n')
      .join(String.raw`\n`)
      .split('\r')
      .join(String.raw`\r`)
      .split('\t')
      .join(String.raw`\t`);
    const parsed = JSON.parse(sanitized);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return <StructuredLogViewer entries={Object.entries(parsed)} />;
    }
  } catch (e) {
    return (
      <div className="pt-1" style={{ color: 'red' }}>
        {t('messages.errorParsingLog')}: {(e as Error).message}
      </div>
    );
  }

  const logLines = logMessage.replaceAll(/[{}]/g, '').split('\n');
  return (
    <div className="pt-1">
      {logLines.map((line, index) => (
        <div key={index + 1}>{line}</div>
      ))}
    </div>
  );
};
